// ✅ src/pages/VehicleV2/VehicleSaleScrapEntry.jsx
// UPDATED FULL FILE
//
// Works for BOTH navigation flows:
//
// 1) From LIST page:
//    navigate("/vehicle/Vehicle_SalesandScrapEntry", {
//      state: { vehicleId, vehicleOwnDetId }
//    })
//
// 2) From LISTING page (edit):
//    navigate("/vehicle/Vehicle_SalesandScrapEntry", {
//      state: { id: row.id, row }
//    })
//
// Expected endpoints:
//   GET  /api/vehicleSaleScrap/init?branchId=&branchType=&loginUserId=
//   GET  /api/vehicleSaleScrap/vehicles/byStore?branchId=&branchType=&storeId=
//   GET  /api/vehicleSaleScrap/vehicle/subtype?branchId=&vehicleId=
//   GET  /api/vehicleSaleScrap/vehicle/regDate?vehicleId=
//   GET  /api/vehicleSaleScrap/vehicle/prefill?vehicleId=
//   GET  /api/vehicleSaleScrap/details?salesscrapId=
//   GET  /api/vehicleSaleScrap/docs?salesscrapId=
//   POST /api/vehicleSaleScrap/upload
//   POST /api/vehicleSaleScrap/submit/sale
//   POST /api/vehicleSaleScrap/submit/scrap
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + initLoading + editLoading + vehicleLoading + submitLoading + uploadLoading
// - safer init / edit / prefill / store reload flow
// - avoids repeated API calls
// - keeps edit-mode dropdown option injection
// - keeps document upload flow

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSave,
  FaUpload,
  FaTrash,
  FaSyncAlt,
  FaInfoCircle,
} from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/salesScrapEntry";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const normalizeSelect = (v, def = "Select") => {
  const s = toStr(v, def);
  return s || def;
};

const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const defaultFormData = {
  soldDate: "",
  storeId: "Select",
  vehicleId: "Select",
  vehicleNameId: "Select",
  vehicleSubTypeId: "Select",
  wantTo: "Sale",
  recommendBy: "",
  soldBy: "",
  scrapBy: "",
  approvedBy: "",
  partyName: "",
  brokerName: "",
  soldPrice: "",
  partyAddress: "",
  remark: "",
  salesscrapId: 0,
};

const toISODateInput = (v) => {
  if (!v) return "";
  if (v instanceof Date && !isNaN(v.getTime())) return v.toISOString().slice(0, 10);

  const s = toStr(v);

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [dd, mm, yy] = s.split("/").map((x) => Number(x));
    const dt = new Date(yy, mm - 1, dd);
    return isNaN(dt.getTime()) ? "" : dt.toISOString().slice(0, 10);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const dt = new Date(s);
  return isNaN(dt.getTime()) ? "" : dt.toISOString().slice(0, 10);
};

const ensureOption = (list = [], opt) => {
  const value = String(opt?.value ?? "");
  if (!value || value === "Select") return list;
  const exists = (list || []).some((x) => String(x.value) === value);
  return exists ? list : [...list, { label: opt.label || value, value }];
};

const normalizeOptionList = (arr, fallbackLabel = "Select") => {
  const list = toList(arr).map((x) => ({
    label: toStr(x.label ?? x.name ?? x.text ?? fallbackLabel, fallbackLabel),
    value: String(x.value ?? x.id ?? x.key ?? fallbackLabel),
  }));
  return list.length ? list : [{ label: fallbackLabel, value: fallbackLabel }];
};

const VehicleSaleScrapEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "BRANCH";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, branchType, userId]
  );

  /* ===========================
     navigation state
  =========================== */
  const navState = location.state || {};
  const editId = navState?.id || navState?.row?.id || 0;
  const prefillVehicleId = navState?.vehicleId || 0;
  const vehicleOwnDetId = navState?.vehicleOwnDetId || 0;
  const isEdit = !!editId;

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const isBusy =
    pageLoading ||
    initLoading ||
    editLoading ||
    vehicleLoading ||
    submitLoading ||
    uploadLoading;

  /* ===========================
     data states
  =========================== */
  const [initData, setInitData] = useState({
    stores: [{ label: "Select", value: "Select" }],
    vehicleCodes: [{ label: "Select", value: "Select" }],
    vehicleNames: [{ label: "Select", value: "Select" }],
  });

  const [vehicleSubTypes, setVehicleSubTypes] = useState([
    { label: "Select", value: "Select" },
  ]);

  const [regDate, setRegDate] = useState("");
  const [docFiles, setDocFiles] = useState([]);
  const [formData, setFormData] = useState(defaultFormData);

  const didPrefillRef = useRef(false);
  const didLoadEditRef = useRef(false);

  /* ===========================
     classes
  =========================== */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const radioLabelClass = useMemo(
    () => `flex items-center gap-2 ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ===========================
     helpers
  =========================== */
  const setField = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const validateSoldDateAgainstReg = useCallback((soldISO, regISO) => {
    if (!soldISO || !regISO) return true;
    return soldISO >= regISO;
  }, []);

  const resetVehicleSelections = useCallback(() => {
    setFormData((p) => ({
      ...p,
      vehicleId: "Select",
      vehicleNameId: "Select",
      vehicleSubTypeId: "Select",
    }));
    setVehicleSubTypes([{ label: "Select", value: "Select" }]);
    setRegDate("");
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /* ===========================
     init load
  =========================== */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      setInitLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: sessionBranchId,
          branchType,
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Failed to load init");
        return;
      }

      setInitData({
        stores: normalizeOptionList(data.stores, "Select"),
        vehicleCodes: normalizeOptionList(data.vehicleCodes, "Select"),
        vehicleNames: normalizeOptionList(data.vehicleNames, "Select"),
      });

      setVehicleSubTypes([{ label: "Select", value: "Select" }]);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Init failed");
    } finally {
      setInitLoading(false);
      setPageLoading(false);
    }
  }, [sessionBranchId, branchType, loginUserId, commonHeaders]);

  /* ===========================
     load vehicles by store
  =========================== */
  const loadVehiclesByStore = useCallback(
    async (storeId, { resetSelections = true } = {}) => {
      const sid = String(storeId || "Select");

      if (resetSelections) {
        setFormData((p) => ({ ...p, storeId: sid }));
        resetVehicleSelections();
      } else {
        setFormData((p) => ({ ...p, storeId: sid }));
      }

      if (isSelect(sid)) return;

      try {
        setVehicleLoading(true);

        const { data } = await axios.get(`${API_BASE}/vehicles/byStore`, {
          params: {
            branchId: sessionBranchId,
            branchType,
            storeId: sid,
          },
          headers: commonHeaders,
        });

        setInitData((prev) => ({
          ...prev,
          vehicleCodes: normalizeOptionList(data?.vehicleCodes, "Select"),
          vehicleNames: normalizeOptionList(data?.vehicleNames, "Select"),
        }));
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err.message || "Store vehicles load failed"
        );
      } finally {
        setVehicleLoading(false);
      }
    },
    [sessionBranchId, branchType, commonHeaders, resetVehicleSelections]
  );

  /* ===========================
     load subtype + reg date
  =========================== */
  const loadVehicleDetails = useCallback(
    async (vehicleId, storeIdForCheck) => {
      const vid = String(vehicleId || "Select");
      const sid = String(storeIdForCheck ?? formData.storeId);

      if (isSelect(sid)) {
        toast.warning("Please select Store first");
        resetVehicleSelections();
        return false;
      }

      setFormData((p) => ({
        ...p,
        vehicleId: vid,
        vehicleNameId: vid,
        vehicleSubTypeId: "Select",
      }));

      setVehicleSubTypes([{ label: "Select", value: "Select" }]);
      setRegDate("");

      if (isSelect(vid)) return false;

      try {
        setVehicleLoading(true);

        const [subRs, regRs] = await Promise.all([
          axios.get(`${API_BASE}/vehicle/subtype`, {
            params: { branchId: sessionBranchId, vehicleId: vid },
            headers: commonHeaders,
          }),
          axios.get(`${API_BASE}/vehicle/regDate`, {
            params: { vehicleId: vid },
            headers: commonHeaders,
          }),
        ]);

        const subtypeList = normalizeOptionList(
          subRs.data?.vehicleSubTypes,
          "Select"
        );
        setVehicleSubTypes(subtypeList);

        const r = regRs.data?.regDate || "";
        setRegDate(r);

        if (formData.soldDate && r && !validateSoldDateAgainstReg(formData.soldDate, r)) {
          toast.error(`Sold/Scrap Date must be >= Registration Date (${r})`);
        }

        return true;
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err.message || "Vehicle details load failed"
        );
        return false;
      } finally {
        setVehicleLoading(false);
      }
    },
    [
      formData.soldDate,
      formData.storeId,
      sessionBranchId,
      commonHeaders,
      validateSoldDateAgainstReg,
      resetVehicleSelections,
    ]
  );

  /* ===========================
     upload docs
  =========================== */
  const handleUploadDocs = useCallback(
    async (files) => {
      if (!files || files.length === 0) return;

      try {
        setUploadLoading(true);

        const fd = new FormData();
        Array.from(files).forEach((f) => fd.append("files", f));

        const { data } = await axios.post(`${API_BASE}/upload`, fd, {
          headers: {
            ...commonHeaders,
            "Content-Type": "multipart/form-data",
          },
        });

        const uploaded = toList(data?.files);
        setDocFiles((prev) => [...prev, ...uploaded]);
        toast.success("Document(s) uploaded");
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || err.message || "Upload failed");
      } finally {
        setUploadLoading(false);
      }
    },
    [commonHeaders]
  );

  const removeDoc = useCallback((idx) => {
    setDocFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  /* ===========================
     input change
  =========================== */
  const handleChange = useCallback(
    async (e) => {
      const { name, value, type, files } = e.target;

      if (name === "storeId") {
        await loadVehiclesByStore(value, { resetSelections: true });
        return;
      }

      if (name === "vehicleId") {
        await loadVehicleDetails(value, formData.storeId);
        return;
      }

      if (name === "vehicleNameId") {
        await loadVehicleDetails(value, formData.storeId);
        return;
      }

      if (type === "file") {
        await handleUploadDocs(files);
        e.target.value = "";
        return;
      }

      if (name === "soldDate") {
        if (regDate && value && !validateSoldDateAgainstReg(value, regDate)) {
          toast.error(`Sold/Scrap Date must be >= Registration Date (${regDate})`);
        }
      }

      setField(name, value);
    },
    [
      formData.storeId,
      regDate,
      setField,
      loadVehiclesByStore,
      loadVehicleDetails,
      handleUploadDocs,
      validateSoldDateAgainstReg,
    ]
  );

  /* ===========================
     edit mode load
  =========================== */
  const loadEditData = useCallback(async () => {
    if (!isEdit || !editId || didLoadEditRef.current) return;

    try {
      didLoadEditRef.current = true;
      setEditLoading(true);

      const [detailsRes, docsRes] = await Promise.all([
        axios.get(`${API_BASE}/details`, {
          params: { salesscrapId: editId },
          headers: commonHeaders,
        }),
        axios.get(`${API_BASE}/docs`, {
          params: { salesscrapId: editId },
          headers: commonHeaders,
        }),
      ]);

      const found = detailsRes.data?.found;
      const row = detailsRes.data?.row;

      if (!found || !row) {
        toast.error("Edit record not found");
        return;
      }

      const storeId = String(row.fld_storeid ?? "Select");
      const vehicleId = String(row.fld_vehicleid ?? "Select");
      const subTypeId = String(row.fld_vehicletypeid ?? "Select");
      const soldDate = toISODateInput(row.entrydate);

      const wantTo =
        String(row.fld_sold_sales || "").toLowerCase() === "sales" ? "Sale" : "Scrap";

      if (!isSelect(storeId)) {
        await loadVehiclesByStore(storeId, { resetSelections: false });
      }

      setInitData((prev) => ({
        ...prev,
        vehicleCodes: ensureOption(prev.vehicleCodes, {
          value: vehicleId,
          label: `Vehicle Code (${vehicleId})`,
        }),
        vehicleNames: ensureOption(prev.vehicleNames, {
          value: vehicleId,
          label: `Vehicle Name (${vehicleId})`,
        }),
      }));

      if (!isSelect(vehicleId)) {
        await loadVehicleDetails(vehicleId, storeId);
      }

      setVehicleSubTypes((prev) =>
        ensureOption(prev, {
          value: subTypeId,
          label: `Type (${subTypeId})`,
        })
      );

      setFormData((p) => ({
        ...p,
        salesscrapId: Number(row.fld_salesscrapid || editId || 0),
        soldDate,
        storeId,
        vehicleId,
        vehicleNameId: vehicleId,
        vehicleSubTypeId: subTypeId,
        wantTo,
        recommendBy: toStr(row.fld_recommendby),
        soldBy: toStr(row.fld_soldby),
        scrapBy: toStr(row.fld_scrapby),
        approvedBy: toStr(row.fld_approvedby),
        partyName: toStr(row.fld_partyname),
        brokerName: toStr(row.fld_brokername),
        soldPrice: toStr(row.fld_soldprice),
        partyAddress: toStr(row.fld_partyaddress),
        remark: toStr(row.fld_remark),
      }));

      const docs = toList(docsRes.data?.docs)
        .map((x) => toStr(x))
        .filter(Boolean)
        .map((fileName) => ({
          fileName,
          originalName: fileName,
          relativePath: `SaleorScrap/${fileName}`,
        }));

      setDocFiles(docs);
      toast.info("Edit data loaded");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Edit load failed");
    } finally {
      setEditLoading(false);
    }
  }, [isEdit, editId, commonHeaders, loadVehiclesByStore, loadVehicleDetails]);

  /* ===========================
     new mode prefill
  =========================== */
  const runPrefillVehicle = useCallback(async () => {
    if (isEdit || !prefillVehicleId || didPrefillRef.current) return;

    try {
      didPrefillRef.current = true;
      const vid = String(prefillVehicleId);

      setInitData((prev) => ({
        ...prev,
        vehicleCodes: ensureOption(prev.vehicleCodes, {
          value: vid,
          label: `Vehicle Code (${vid})`,
        }),
        vehicleNames: ensureOption(prev.vehicleNames, {
          value: vid,
          label: `Vehicle Name (${vid})`,
        }),
      }));

      setFormData((p) => ({
        ...p,
        vehicleId: vid,
        vehicleNameId: vid,
      }));

      // optional prefill fetch if backend returns more info
      try {
        const { data } = await axios.get(`${API_BASE}/vehicle/prefill`, {
          params: { vehicleId: vid },
          headers: commonHeaders,
        });

        const row = data?.row;
        if (data?.found && row?.fld_storeid) {
          const storeId = String(row.fld_storeid);

          setFormData((p) => ({
            ...p,
            storeId,
            vehicleId: String(row.fld_vehicleid ?? vid),
            vehicleNameId: String(row.fld_vehicleid ?? vid),
          }));

          await loadVehiclesByStore(storeId, { resetSelections: false });

          setInitData((prev) => ({
            ...prev,
            vehicleCodes: ensureOption(prev.vehicleCodes, {
              value: String(row.fld_vehicleid ?? vid),
              label: String(row.fld_vehicle_code ?? `Vehicle Code (${vid})`),
            }),
            vehicleNames: ensureOption(prev.vehicleNames, {
              value: String(row.fld_vehicleid ?? vid),
              label: String(row.fld_vehicle_name ?? `Vehicle Name (${vid})`),
            }),
          }));

          await loadVehicleDetails(String(row.fld_vehicleid ?? vid), storeId);
        } else {
          toast.info("Vehicle prefilled. Please select Store to load subtype.");
        }
      } catch {
        toast.info("Vehicle prefilled. Please select Store to load subtype.");
      }
    } catch (err) {
      console.error(err);
    }
  }, [isEdit, prefillVehicleId, commonHeaders, loadVehiclesByStore, loadVehicleDetails]);

  /* ===========================
     submit
  =========================== */
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (isSelect(formData.storeId)) return toast.error("Please select Store");
      if (isSelect(formData.vehicleId))
        return toast.error("Please select Vehicle Code/Name");
      if (!formData.soldDate) return toast.error("Please select Sold/Scrap Date");
      if (isSelect(formData.vehicleSubTypeId))
        return toast.error("Please select Vehicle Type/Subtype");
      if (!formData.approvedBy) return toast.error("Approved By is required");
      if (!formData.partyName) return toast.error("Party Name is required");

      if (formData.wantTo === "Sale" && !formData.soldBy) {
        return toast.error("Sold By is required");
      }

      if (formData.wantTo === "Scrap" && !formData.scrapBy) {
        return toast.error("Scrap By is required");
      }

      if (regDate && !validateSoldDateAgainstReg(formData.soldDate, regDate)) {
        return toast.error(
          `Sold/Scrap Date must be >= Registration Date (${regDate})`
        );
      }

      const docs = docFiles.map((d) => d.fileName).filter(Boolean);

      const payloadBase = {
        loginUserId,
        branchId: sessionBranchId,
        storeId: formData.storeId,
        vehicleId: formData.vehicleId,
        vehicleSubTypeId: formData.vehicleSubTypeId,
        soldDate: formData.soldDate,
        recommendBy: formData.recommendBy,
        approvedBy: formData.approvedBy,
        soldPrice: formData.soldPrice,
        partyName: formData.partyName,
        partyAddress: formData.partyAddress,
        brokerName: formData.brokerName,
        remark: formData.remark,
        docs,
        salesscrapId: formData.salesscrapId || 0,
        vehicleOwnDetId,
      };

      const isSale = formData.wantTo === "Sale";
      const url = isSale
        ? `${API_BASE}/submit/sale`
        : `${API_BASE}/submit/scrap`;

      const payload = isSale
        ? { ...payloadBase, soldBy: formData.soldBy }
        : { ...payloadBase, scrapBy: formData.scrapBy };

      try {
        setSubmitLoading(true);

        const { data } = await axios.post(url, payload, {
          headers: commonHeaders,
        });

        toast.success(
          data?.message || (isEdit ? "Updated Successfully" : "Saved Successfully")
        );
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || err.message || "Submit failed");
      } finally {
        setSubmitLoading(false);
      }
    },
    [
      formData,
      regDate,
      docFiles,
      loginUserId,
      sessionBranchId,
      vehicleOwnDetId,
      isEdit,
      commonHeaders,
      validateSoldDateAgainstReg,
    ]
  );

  /* ===========================
     refresh current form-related data
  =========================== */
  const handleRefresh = useCallback(async () => {
    await loadInit();

    if (isEdit) {
      didLoadEditRef.current = false;
      await loadEditData();
      return;
    }

    if (prefillVehicleId) {
      didPrefillRef.current = false;
      await runPrefillVehicle();
    }
  }, [loadInit, isEdit, loadEditData, prefillVehicleId, runPrefillVehicle]);

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (initLoading) return;
    if (!isEdit) return;
    loadEditData();
  }, [initLoading, isEdit, loadEditData]);

  useEffect(() => {
    if (initLoading) return;
    if (isEdit) return;
    if (!prefillVehicleId) return;
    runPrefillVehicle();
  }, [initLoading, isEdit, prefillVehicleId, runPrefillVehicle]);

  const showSaleFields = formData.wantTo === "Sale";
  const showScrapFields = formData.wantTo === "Scrap";

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE SALE AND SCRAP</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Sales and Scrap Entry
                {isEdit ? ` (Edit #${editId})` : ""}
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {sessionBranchId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Mode: {isEdit ? "EDIT" : "NEW"}</span>
                <span className={pillClass}>Prefill Vehicle: {prefillVehicleId || "-"}</span>
                <span className={pillClass}>VehicleOwnDetId: {vehicleOwnDetId || "-"}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {(pageLoading || initLoading) ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3
                className={`text-xl font-semibold mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-800"
                }`}
              >
                Loading...
              </h3>
              <p
                className={`mb-6 max-w-sm mx-auto ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left */}
                <div className="space-y-6">
                  <div>
                    <label className={labelClass}>Sold/Scrap Date *</label>
                    <input
                      type="date"
                      name="soldDate"
                      value={formData.soldDate}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy}
                    />
                    {regDate ? (
                      <div className={`mt-2 text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Registration Date: <span className="font-semibold">{regDate}</span>
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className={labelClass}>Store *</label>
                    <select
                      name="storeId"
                      value={formData.storeId}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy}
                    >
                      {(initData.stores || []).map((s, idx) => (
                        <option key={`${s.value}-${idx}`} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Vehicle Code *</label>
                    <select
                      name="vehicleId"
                      value={formData.vehicleId}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy}
                    >
                      {(initData.vehicleCodes || []).map((v, idx) => (
                        <option key={`${v.value}-${idx}`} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Vehicle Name *</label>
                    <select
                      name="vehicleNameId"
                      value={formData.vehicleNameId}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy}
                    >
                      {(initData.vehicleNames || []).map((v, idx) => (
                        <option key={`${v.value}-${idx}`} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Vehicle Type/Subtype *</label>
                    <select
                      name="vehicleSubTypeId"
                      value={formData.vehicleSubTypeId}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy || isSelect(formData.vehicleId)}
                    >
                      {(vehicleSubTypes || []).map((v, idx) => (
                        <option key={`${v.value}-${idx}`} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Upload Document(s)</label>
                    <div className="flex items-center gap-3">
                      <label
                        className={`flex-1 cursor-pointer ${inputClass} flex items-center gap-2 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaUpload className="w-4 h-4" />
                        <span className="truncate">
                          {uploadLoading ? "Uploading..." : "Choose files..."}
                        </span>
                        <input
                          type="file"
                          name="docsUpload"
                          onChange={handleChange}
                          className="hidden"
                          multiple
                          disabled={isBusy}
                        />
                      </label>
                    </div>

                    {docFiles.length > 0 ? (
                      <div className="mt-3 space-y-2">
                        {docFiles.map((d, idx) => (
                          <div
                            key={`${d.fileName}-${idx}`}
                            className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                              isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"
                            }`}
                          >
                            <div className="text-sm truncate pr-3">
                              {d.originalName || d.fileName}
                            </div>

                            <div className="flex items-center gap-2">
                              {d.relativePath ? (
                                <a
                                  href={`/${d.relativePath}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={`text-xs underline ${
                                    isDark ? "text-gray-200" : "text-gray-700"
                                  }`}
                                >
                                  Open
                                </a>
                              ) : null}

                              <button
                                type="button"
                                onClick={() => removeDoc(idx)}
                                disabled={isBusy}
                                className={`px-3 py-1 rounded-md text-sm flex items-center gap-2 ${
                                  isDark
                                    ? "bg-white/10 hover:bg-white/20"
                                    : "bg-gray-200 hover:bg-gray-300"
                                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                              >
                                <FaTrash className="w-3 h-3" />
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className={labelClass}>Want To</label>
                    <div className="flex gap-6">
                      <label className={radioLabelClass}>
                        <input
                          type="radio"
                          name="wantTo"
                          value="Sale"
                          checked={formData.wantTo === "Sale"}
                          onChange={handleChange}
                          disabled={isBusy}
                        />
                        Sale
                      </label>
                      <label className={radioLabelClass}>
                        <input
                          type="radio"
                          name="wantTo"
                          value="Scrap"
                          checked={formData.wantTo === "Scrap"}
                          onChange={handleChange}
                          disabled={isBusy}
                        />
                        Scrap
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Recommend By</label>
                    <input
                      type="text"
                      name="recommendBy"
                      value={formData.recommendBy}
                      onChange={handleChange}
                      className={inputClass}
                      disabled={isBusy}
                    />
                  </div>

                  {showSaleFields ? (
                    <div>
                      <label className={labelClass}>Sold By *</label>
                      <input
                        type="text"
                        name="soldBy"
                        value={formData.soldBy}
                        onChange={handleChange}
                        className={inputClass}
                        required
                        disabled={isBusy}
                      />
                    </div>
                  ) : null}

                  {showScrapFields ? (
                    <div>
                      <label className={labelClass}>Scrap By *</label>
                      <input
                        type="text"
                        name="scrapBy"
                        value={formData.scrapBy}
                        onChange={handleChange}
                        className={inputClass}
                        required
                        disabled={isBusy}
                      />
                    </div>
                  ) : null}

                  <div>
                    <label className={labelClass}>Approved By *</label>
                    <input
                      type="text"
                      name="approvedBy"
                      value={formData.approvedBy}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy}
                    />
                  </div>
                </div>

                {/* Right */}
                <div className="space-y-6">
                  <div>
                    <label className={labelClass}>Party Name *</label>
                    <input
                      type="text"
                      name="partyName"
                      value={formData.partyName}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Broker Name (if any)</label>
                    <input
                      type="text"
                      name="brokerName"
                      value={formData.brokerName}
                      onChange={handleChange}
                      className={inputClass}
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Sold Price</label>
                    <input
                      type="number"
                      name="soldPrice"
                      value={formData.soldPrice}
                      onChange={handleChange}
                      className={inputClass}
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Party Address</label>
                    <textarea
                      name="partyAddress"
                      value={formData.partyAddress}
                      onChange={handleChange}
                      rows={3}
                      className={inputClass}
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Remarks / Reason</label>
                    <textarea
                      name="remark"
                      value={formData.remark}
                      onChange={handleChange}
                      rows={3}
                      className={inputClass}
                      disabled={isBusy}
                    />
                  </div>

                  <div
                    className={`text-xs rounded-lg p-3 border ${
                      isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div><b>Mode:</b> {isEdit ? "EDIT" : "NEW"}</div>
                    <div><b>EditId:</b> {String(editId || "")}</div>
                    <div><b>PrefillVehicleId:</b> {String(prefillVehicleId || "")}</div>
                    <div><b>VehicleOwnDetId:</b> {String(vehicleOwnDetId || "")}</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-center">
                <button
                  type="submit"
                  disabled={isBusy}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isBusy ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSave className="w-4 h-4" />
                  {submitLoading
                    ? isEdit
                      ? "Updating..."
                      : "Submitting..."
                    : isEdit
                    ? "Update"
                    : "Submit"}
                </button>
              </div>
            </form>
          )}

          {isBusy && (
            <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {pageLoading
                ? "Loading page..."
                : initLoading
                ? "Loading initial data..."
                : editLoading
                ? "Loading edit data..."
                : vehicleLoading
                ? "Loading vehicle details..."
                : uploadLoading
                ? "Uploading documents..."
                : submitLoading
                ? "Submitting..."
                : "Loading..."}
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSaleScrapEntry;